<script setup>
import { Icon } from '@iconify/vue2'
import { computed, useAttrs } from 'vue'
import Spinner from '../spinner/Spinner.vue'

// interface Props {
//   text?: string,
//   loading?: boolean,
//   transform?: string,
//   icon?: string,
//   rightIcon?: string
// }

const emit = defineEmits(['click'])

const props = defineProps({
	text: { type: String },
	loading: { type: Boolean, default: false },
	transform: { type: String, default: '' },
	icon: { type: String },
	rightIcon: { type: String },
	active: { type: Boolean, default: false },
})

const { disabled, secondary, danger, success, caution, back, link } = useAttrs()

const styleType = computed(() => {
	return {
		secondary: !isUndefined(secondary),
		danger: !isUndefined(danger),
		success: !isUndefined(success),
		caution: !isUndefined(caution),
		back: !isUndefined(back),
		link: !isUndefined(link),
	}
})

const isUndefined = (value) => {
	return value === undefined
}

const theme = computed(() => {
	const active = Object.entries(styleType.value).find(([, flag]) => {
		return flag
	})
	return active ? active[0] : 'primary'
})
</script>

<template>
	<button
		:disabled="disabled"
		:class="[styleType, transform, { active: props.active }]"
		@click="emit('click')"
	>
		<Icon
			v-if="!loading && icon"
			:icon="icon"
			name="icon"
		/>
		<span v-if="!loading && text">{{ text }}</span>
		<Icon
			v-if="!loading && rightIcon"
			:icon="rightIcon"
			name="icon"
		/>
		<Spinner
			v-if="loading"
			:theme="theme"
			class="w-5 h-fit"
		/>
	</button>
</template>

<style scoped>
button {
	border: 1px solid !important;
}
button {
	font-family: 'Open Sans', sans-serif;
	@apply cursor-pointer font-semibold px-2 py-1 bg-green-base !border !border-black-neutral rounded flex items-center gap-2 text-sm h-fit relative text-black-neutral;
}

button:focus,
button:focus.secondary,
button:focus.success,
button:focus.caution,
button:focus.danger {
	@apply shadow-inner;
}

button:active {
	@apply shadow-inner shadow-black-neutral;
}

button:disabled {
	@apply opacity-40 hover:shadow-none cursor-not-allowed;
}

button:disabled:active.secondary {
	@apply bg-transparent text-blue-base;
}

button:disabled:hover.secondary {
	@apply shadow-none;
}

button:hover,
button:hover.secondary,
button:hover.danger,
button:hover.caution,
button:hover.success {
	@apply shadow-md;
}

button.secondary {
	@apply !bg-white-neutral !text-blue-base !border !border-blue-base;
}

button:active.secondary,
button.secondary.active {
	@apply bg-blue-900 text-white-neutral !important;
}

button.danger {
	@apply bg-white-neutral text-red-danger !border !border-red-danger;
}

button:active.danger {
	@apply bg-red-danger text-white-neutral;
}

button.danger.link {
	@apply text-red-danger;
}

button.success.link {
	@apply text-green-success-dark;
}

button.success {
	@apply bg-white-neutral text-green-success-dark !border border-green-success-dark;
}

button:active.success {
	@apply bg-green-success-dark text-white-neutral;
}

button.caution {
	@apply bg-yellow-caution-light text-yellow-caution-medium !border border-yellow-caution-medium;
}

button:active.caution {
	@apply bg-yellow-caution-medium text-yellow-caution-light;
}

button.back {
	@apply border-none bg-white text-blue-base;
}

button.link {
	@apply !border-none !bg-transparent text-blue-base;
}

button.link:hover {
	@apply border-b border-blue-base shadow-none;
}

button.link:active {
	@apply text-black-neutral;
}

button:not([disabled]).link:hover::after {
	content: '';
	height: 1px;
	background-color: #01395e;
	width: 100%;
	display: block;
	position: absolute;
	bottom: 0;
	left: 0;
}

button:not([disabled]).danger.link:hover::after {
	content: '';
	height: 1px;
	background-color: #c60020;
	width: 100%;
	display: block;
	position: absolute;
	bottom: 0;
	left: 0;
}

button:not([disabled]).success.link:hover::after {
	content: '';
	height: 1px;
	background-color: #138600;
	width: 100%;
	display: block;
	position: absolute;
	bottom: 0;
	left: 0;
}
</style>
